import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import useSWR from "swr";
import { fetchFruits, fetchStats } from "../lib/api";
import { Reveal, StaggerGroup, MaskedLines } from "../components/Reveal";
import Counter from "../components/Counter";
import FruitCard from "../components/FruitCard";
import PartnerMarquee from "../components/PartnerMarquee";
import InSeasonNow from "../components/InSeasonNow";
import { CONTACT, BUYER_PROMISE } from "../lib/brand";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const ease = [0.22, 1, 0.36, 1];

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] overflow-hidden bg-cream-100">
      {/* fruit imagery, anchored to the right with a soft cream wash on the left */}
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img
          src="/hero-fruits.webp"
          alt="Fresh seasonal fruit"
          className="h-full w-full object-cover object-right"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-cream-100 via-cream-100/85 to-cream-100/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-cream-100/55 to-transparent" />
      </motion.div>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-cream-100 to-transparent" />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-center px-5 pt-28 pb-16 md:px-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="w-fit"
        >
          <div className="font-display text-6xl font-semibold leading-none tracking-[0.1em] text-forest lg:text-7xl">
            VENYA
          </div>
          <div className="mt-2 flex w-full justify-between font-display text-sm font-medium text-gold sm:text-base">
            {"TRADE ENTERPRISES".split("").map((c, i) => (
              <span key={i}>{c === " " ? "\u00A0" : c}</span>
            ))}
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55, duration: 0.7 }}
          className="mt-6 text-base text-forest-light"
        >
          Wholesale · Import · Export
        </motion.p>

        <h1 className="mt-4 max-w-2xl font-display text-3xl font-medium leading-[1.08] text-forest sm:text-4xl lg:text-5xl">
          <MaskedLines
            lines={["Bringing the finest fruit from", "around the world to you."]}
            delay={0.6}
          />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/calendar"
            data-testid="hero-calendar-btn"
            className="group flex items-center gap-2 rounded-full bg-forest px-6 py-3.5 text-cream-100 transition-colors duration-300 hover:bg-forest/90"
          >
            <span className="text-cream-100 tracking-wide" style={{ fontSize: "0.8rem" }}>
              View the sourcing calendar
            </span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/products"
            data-testid="hero-products-btn"
            className="group flex items-center gap-2 rounded-full border border-forest/30 px-6 py-3.5 text-forest transition-colors duration-300 hover:border-forest"
          >
            <span className="tracking-wide" style={{ fontSize: "0.8rem" }}>
              What we carry
            </span>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}

function StatsBand({ stats }) {
  const items = [
    { value: stats?.total_varieties ?? 220, suffix: "+", label: "Varieties tracked" },
    { value: stats?.total_families ?? 13, suffix: "+", label: "Fruit families" },
    { value: stats?.indian_states_count ?? 19, suffix: "+", label: "Indian growing states" },
    { value: stats?.generations ?? 2, suffix: "", label: "Generations of trade" },
  ];
  return (
    <section data-testid="stats-band" className="border-y border-border bg-cream-200">
      <div className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-20">
        <Reveal className="mb-12 max-w-2xl">
          <h2 className="font-display text-2xl font-medium leading-tight text-forest md:text-4xl">
            What "Venya Trade Enterprises" adds up to — counted from our live
            sourcing catalogue.
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-border bg-border lg:grid-cols-4">
          {items.map((it) => (
            <div key={it.label} className="bg-cream-100 p-8 md:p-10">
              <div className="font-display text-5xl font-semibold text-forest md:text-6xl">
                <Counter value={it.value} suffix={it.suffix} />
              </div>
              <div className="mt-3 font-sans text-sm text-forest-muted">
                {it.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatWeCarry({ fruits }) {
  return (
    <section data-testid="what-we-carry" className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-36">
      <Reveal className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl font-medium leading-[1.05] text-forest md:text-5xl">
            Fruit families, sourced from both Indian orchards and international
            growers — so the calendar rarely runs empty.
          </h2>
        </div>
        <Link
          to="/products"
          className="group flex flex-none items-center gap-2 self-start text-forest md:self-end"
        >
          <span className="tracking-wide text-sm">All families</span>
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>

      <StaggerGroup className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {(fruits || []).map((f, i) => (
          <FruitCard key={f.slug} fruit={f} index={i} />
        ))}
      </StaggerGroup>
    </section>
  );
}

function BuyerPromise() {
  return (
    <section className="bg-forest text-cream-100">
      <div className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-36">
        <div className="grid gap-16 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <h2 className="font-display text-3xl font-medium leading-[1.05] md:text-5xl">
              Freshness isn't a promise here — <span className="italic text-gold">it's a habit.</span>
            </h2>
            <p className="mt-8 max-w-md text-cream-100/70">
              Built on {CONTACT.parent}'s grower and importer network. Every lot is inspected
              at the Patiala desk before it ships.
            </p>
          </Reveal>
          <StaggerGroup className="flex flex-col divide-y divide-cream-100/15 border-t border-cream-100/15">
            {BUYER_PROMISE.map((point, i) => (
              <motion.div
                key={i}
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                className="flex gap-6 py-7"
              >
                <span className="font-mono text-sm text-gold">0{i + 1}</span>
                <p className="text-lg leading-relaxed text-cream-100/90">{point}</p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </div>
    </section>
  );
}

function PartnersSection() {
  return (
    <section data-testid="partners-section" className="border-y border-border py-20 md:py-28">
      <Reveal className="mx-auto mb-12 max-w-[1400px] px-5 text-center md:px-10">
        <h2 className="font-display text-3xl font-medium text-forest md:text-5xl">
          Sourced worldwide
        </h2>
      </Reveal>
      <PartnerMarquee />
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
      <Reveal className="relative overflow-hidden rounded-sm border border-border bg-cream-200 p-10 md:p-20">
        <Sparkles className="absolute right-8 top-8 h-6 w-6 text-gold/50" />
        <h2 className="max-w-3xl font-display text-3xl font-medium leading-[1.05] text-forest md:text-5xl">
          Let's put fruit on your shelves.
        </h2>
        <p className="mt-6 max-w-xl text-forest-light">
          Tell us what you buy, how often, and where it needs to land. We'll come back with
          sourcing options and a quote from the next available harvest window.
        </p>
        <Link
          to="/contact"
          data-testid="cta-enquire-btn"
          className="group mt-10 inline-flex items-center gap-3 rounded-full bg-forest px-7 py-4 text-cream-100 transition-colors duration-300 hover:bg-forest/90"
        >
          <span className="text-cream-100 tracking-wide" style={{ fontSize: "0.8rem" }}>
            Start an enquiry
          </span>
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </section>
  );
}

export default function Home() {
  const { data: fruits } = useSWR("fruits", fetchFruits);
  const { data: stats } = useSWR("stats", fetchStats);

  return (
    <>
      <Hero />
      <StatsBand stats={stats} />
      <InSeasonNow />
      <WhatWeCarry fruits={fruits} />
      <BuyerPromise />
      <PartnersSection />
      <CtaBanner />
    </>
  );
}
